import React from 'react';
import { Link } from 'react-router-dom';
import { Icon, Button, Badge, Card, CardBody } from '@repo/ui';
import type {
  IntegrationCardProps,
  ModuleHealth,
} from '@xtnd/web/shared/types/tenantSettingTypes';
import type { ModuleConfigurationStatus } from '@xtnd/web/features/settings/api';

// Icon mapping for different module types
import type { IconName } from '@repo/ui';

const MODULE_ICONS: Record<string, IconName> = {
  clioAPI: 'HiDocumentText',
  qboAPI: 'HiDocumentText',
  tenantSettings: 'HiCog',
  archival: 'HiArchive',
  juniper: 'HiCloudUpload',
  documentArchival: 'HiCloudUpload',
  templateManager: 'HiDocumentText',
  reporting: 'HiChartBar',
  default: 'HiCog',
};


// Color schemes for different module types
const MODULE_COLORS: Record<string, { bg: string; icon: string; hover: string }> = {
  clioAPI: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    icon: 'text-blue-600 dark:text-blue-400',
    hover: 'hover:border-blue-300 dark:hover:border-blue-700',
  },
  qboAPI: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    icon: 'text-green-600 dark:text-green-400',
    hover: 'hover:border-green-300 dark:hover:border-green-700',
  },
  tenantSettings: {
    bg: 'bg-xtnd-light dark:bg-xtnd-blue/10',
    icon: 'text-xtnd-blue',
    hover: 'hover:border-xtnd-blue',
  },
  archival: {
    bg: 'bg-purple-100 dark:bg-purple-900/30',
    icon: 'text-purple-600 dark:text-purple-400',
    hover: 'hover:border-purple-300 dark:hover:border-purple-700',
  },
  juniper: {
    bg: 'bg-indigo-100 dark:bg-indigo-900/30',
    icon: 'text-indigo-600 dark:text-indigo-400',
    hover: 'hover:border-indigo-300 dark:hover:border-indigo-700',
  },
  documentArchival: {
    bg: 'bg-purple-100 dark:bg-purple-900/30',
    icon: 'text-purple-600 dark:text-purple-400',
    hover: 'hover:border-purple-300 dark:hover:border-purple-700',
  },
  templateManager: {
    bg: 'bg-teal-100 dark:bg-teal-900/30',
    icon: 'text-teal-600 dark:text-teal-400',
    hover: 'hover:border-teal-300 dark:hover:border-teal-700',
  },
  reporting: {
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    icon: 'text-orange-600 dark:text-orange-400',
    hover: 'hover:border-orange-300 dark:hover:border-orange-700',
  },
  default: {
    bg: 'bg-gray-100 dark:bg-gray-900/30',
    icon: 'text-gray-600 dark:text-gray-400',
    hover: 'hover:border-gray-300 dark:hover:border-gray-700',
  },
};

// Navigation routes for modules that should navigate instead of opening modals
const MODULE_NAVIGATION_ROUTES: Record<string, string> = {
  archival: '/settings/archival',
  juniper: '/settings/juniper-config',
  templateManager: '/settings/template-manager',
  reporting: '/settings/reporting-config',
};

interface IntegrationCardWithHealthProps extends IntegrationCardProps {
  health?: ModuleHealth;
  connectionStatus?: {
    connected: boolean;
    apiStatus: string;
    clioConfigured: boolean;
    error?: string;
  };
  configurationStatus?: ModuleConfigurationStatus;
}

export const IntegrationCard: React.FC<IntegrationCardWithHealthProps> = ({
  feature,
  onConfigure,
  health,
  connectionStatus,
  configurationStatus,
}) => {
  // Get appropriate icon for the module
  const iconName: IconName = (MODULE_ICONS[feature.moduleName] || MODULE_ICONS.default) as IconName;
  const colors = MODULE_COLORS[feature.moduleName] || MODULE_COLORS.default || {
    bg: 'bg-gray-100 dark:bg-gray-900/30',
    icon: 'text-gray-600 dark:text-gray-400',
    hover: 'hover:border-gray-300 dark:hover:border-gray-700',
  };

  // Determine status based on configuration and health
  const getStatus = () => {
    if (!feature.enabled) {
      return { type: 'disabled', text: 'Disabled', variant: 'secondary' as const };
    }

    // Priority 1: Use configuration status if available (for supported modules)
    if (
      configurationStatus &&
      (feature.moduleName === 'juniper' ||
        feature.moduleName === 'clioAPI' ||
        feature.moduleName === 'qboAPI' ||
        feature.moduleName === 'tenantSettings' ||
        feature.moduleName === 'archival')
    ) {
      if (configurationStatus.isConfigured) {
        return { type: 'configured', text: 'Configured', variant: 'success' as const };
      } else {
        const errorText = configurationStatus.requiresAttention
          ? 'Needs Attention'
          : 'Not Configured';
        return { type: 'not-configured', text: errorText, variant: 'warning' as const };
      }
    }

    // Priority 2: Fall back to connectionStatus for backward compatibility
    if (feature.moduleName === 'clioAPI' && connectionStatus) {
      if (!connectionStatus.connected) {
        return { type: 'error', text: 'Disconnected', variant: 'error' as const };
      }

      if (connectionStatus.clioConfigured) {
        return { type: 'connected', text: 'Connected', variant: 'success' as const };
      }

      return { type: 'not-connected', text: 'Not Configured', variant: 'warning' as const };
    }

    // Priority 3: Special handling for templateManager - show blank when not configured
    if (feature.moduleName === 'templateManager') {
      if (feature.configured) {
        return { type: 'connected', text: 'Active', variant: 'success' as const };
      } else {
        return { type: 'blank', text: 'Ready', variant: 'default' as const };
      }
    }

    // Priority 4: Original health-based logic for other modules
    if (health?.status === 'checking') {
      return { type: 'checking', text: 'Checking...', variant: 'default' as const };
    }

    if (health?.status === 'connected' && feature.configured) {
      return { type: 'connected', text: 'Active', variant: 'success' as const };
    }

    if (health?.status === 'error') {
      return { type: 'error', text: 'Error', variant: 'error' as const };
    }

    // Don't show "Not Connected" for internal modules
    if (feature.moduleName === 'templateManager' || feature.moduleName === 'reporting') {
      return { type: 'blank', text: 'Ready', variant: 'default' as const };
    }

    return { type: 'not-connected', text: 'Not Connected', variant: 'warning' as const };
  };

  const status = getStatus();

  // Get description based on module type
  const getDescription = () => {
    switch (feature.moduleName) {
      case 'clioAPI':
        return 'Automate invoice processing with Clio integration';
      case 'qboAPI':
        return 'Sync accounting data with QuickBooks Online';
      case 'tenantSettings':
        return 'Manage organization settings and preferences';
      case 'archival':
        return 'Configure cloud storage providers for archival';
      case 'juniper':
        return 'Monitor and manage invoice processing pipeline';
      case 'documentArchival':
        return 'Set up document storage and archival policies';
      case 'reporting':
        return 'Configure AR reporting and notifications';
      default:
        return 'Configure integration settings';
    }
  };

  const handleConfigure = () => {
    onConfigure(feature.moduleName);
  };

  // Check if this module should navigate instead of opening a modal
  const navigationRoute = MODULE_NAVIGATION_ROUTES[feature.moduleName];
  const shouldNavigate = !!navigationRoute;

  return (
    <Card
      className={`
        group relative
        ${!feature.enabled ? 'opacity-60' : ''}
      `}
      padding="lg"
      variant="outlined"
    >
      <CardBody>
        {/* Header with Icon, Title and Badge */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`flex-shrink-0 p-3 ${colors.bg} rounded-xl`}>
              <Icon name={iconName} size="lg" className={colors.icon} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {feature.displayName}
            </h3>
          </div>
          <Badge variant={status.variant} className="text-xs flex-shrink-0 ml-2">
            {status.text}
          </Badge>
        </div>

        {/* Description */}
        <div className="mb-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
            {getDescription()}
          </p>
          {health?.message && (
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-2 italic">
              {health.message}
            </p>
          )}
        </div>

        {/* Action Button */}
        <div className="mt-auto">
          {shouldNavigate ? (
            <Link to={navigationRoute} className="block">
              <Button
                variant="outline"
                size="sm"
                disabled={!feature.enabled}
                leftIcon={<Icon name="HiCog" className="h-4 w-4" />}
                className="w-full"
              >
                Configure
              </Button>
            </Link>
          ) : (
            <Button
              onClick={handleConfigure}
              variant="outline"
              size="sm"
              disabled={!feature.enabled}
              leftIcon={<Icon name="HiCog" className="h-4 w-4" />}
              className="w-full"
            >
              Configure
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
};
